import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';

export default function VerifyOTP() {
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    const { setAuth } = useAuth();
    const { isPortal } = useTenant();

    const queryParams = new URLSearchParams(window.location.search);
    const email = location.state?.email || queryParams.get('email');
    const type = location.state?.type || queryParams.get('type') || 'signup';

    useEffect(() => {
        if (!email) {
            navigate('/login');
        }
    }, [email, navigate]);

    const getBaseHost = () => {
        const hostname = window.location.hostname;
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            return window.location.host;
        }
        if (hostname.endsWith('aaharamcloud.com')) {
            return 'aaharamcloud.com';
        }
        if (hostname.endsWith('aaharamcloud.vercel.app')) {
            return 'aaharamcloud.vercel.app';
        }
        if (hostname.endsWith('savorybites.vercel.app')) {
            return 'savorybites.vercel.app';
        }
        const parts = hostname.split('.');
        if (parts.length > 2) {
            return parts.slice(-2).join('.');
        }
        return window.location.host;
    };

    const navigateBasedOnRole = (user, token) => {
        const urlParams = new URLSearchParams(window.location.search);
        const redirectQuery = urlParams.get('redirect');
        const branchQuery = urlParams.get('branch');

        if (branchQuery) {
            localStorage.setItem('active_branch_slug', branchQuery);
        }

        if (user.role === 'franchise_admin' && user.franchise_slug) {
            if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                window.location.href = `${window.location.origin}/?restaurant=${user.franchise_slug}`;
            } else {
                const baseHost = getBaseHost();
                const protocol = window.location.protocol;
                window.location.href = `${protocol}//${user.franchise_slug}.${baseHost}/`;
            }
            return;
        }

        const targetSlug = branchQuery || user.slug || localStorage.getItem('active_branch_slug');
        const customUrl = user.custom_frontend_url;

        if (user.role !== 'super_admin' && (targetSlug || customUrl)) {
            const baseHost = getBaseHost();
            const protocol = window.location.protocol;
            
            let domain = '';
            if (customUrl) {
                domain = customUrl.replace(/\/$/, '');
            } else if (targetSlug && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
                domain = `${protocol}//${targetSlug}.${baseHost}`;
            }

            let path = redirectQuery || '/';
            if (!redirectQuery) {
                if (user.role === 'restaurant_admin') path = '/admin';
                else if (user.role === 'kitchen_staff') path = '/kitchen';
                else if (user.role === 'delivery_partner') path = '/delivery';
                else if (user.role === 'customer' || !user.role) path = '/orders';
            }

            if (domain && domain !== window.location.origin) {
                window.location.href = `${domain}/login?token=${token}&redirect=${path}`;
                return;
            }
        }

        if (user.role === 'super_admin') {
            navigate('/super-admin');
        } else if (user.role === 'restaurant_admin') {
            navigate(redirectQuery || '/admin');
        } else if (user.role === 'kitchen_staff') {
            navigate(redirectQuery || '/kitchen');
        } else if (user.role === 'delivery_partner') {
            navigate(redirectQuery || '/delivery');
        } else {
            navigate(redirectQuery || '/orders');
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await api.post('/api/auth/verify-otp', { email, otp, type });
            toast.success(res.data.message);

            if (res.data.token) {
                // For signup/login verification
                localStorage.setItem('token', res.data.token);
                setAuth(res.data.token, res.data.user);
                navigateBasedOnRole(res.data.user, res.data.token);
            } else if (type === 'forgot_password' || type === 'password_update') {
                navigate('/reset-password', { state: { email, otp, type } });
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Verification failed');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResendLoading(true);
        try {
            await api.post('/api/auth/request-otp', { email, type, isResend: true });
            if (location.state?.channel === 'sms') {
                toast.success('OTP resent to your mobile number');
            } else {
                toast.success('OTP resent to your email');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to resend OTP');
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-extrabold text-gray-900">
                        {location.state?.channel === 'sms' ? 'Verify Mobile' : 'Verify Email'}
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        {location.state?.channel === 'sms' 
                            ? "We've sent a 6-digit code to your mobile number" 
                            : <>We've sent a 6-digit code to <span className="font-semibold text-gray-900">{email}</span></>
                        }
                    </p>
                </div>

                <form onSubmit={handleVerify} className="space-y-6">
                    <div>
                        <input
                            type="text"
                            maxLength="6"
                            placeholder="6-digit code"
                            className="w-full px-5 py-4 text-center text-2xl font-bold tracking-[1em] border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-0 transition-all outline-none"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading || otp.length !== 6}
                        className="w-full py-4 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-primary-200 transition-all disabled:opacity-50 disabled:shadow-none"
                    >
                        {loading ? 'Verifying...' : 'Verify Now'}
                    </button>
                </form>

                <div className="mt-8 text-center">
                    <button
                        onClick={handleResend}
                        disabled={resendLoading}
                        className="text-primary-600 font-semibold hover:text-primary-700 transition-colors disabled:opacity-50"
                    >
                        {resendLoading ? 'Resending code...' : "Didn't receive code? Resend"}
                    </button>
                </div>
            </div>
        </div>
    );
}
